import { AskMaraCard } from "@monthly/ui";
import { SouthOutlined, TimelineRounded } from "@mui/icons-material";
import { Card, Divider, Stack, styled, Typography } from "@mui/material";

import type { UNIDADES_OPTIONS } from "~/components/feature/control-panel/ControlPanel";
import type { PotentialSavingsData } from "~/lib/types/filteredDataMetrics";
import { formatNumberWithSeparators } from "~/utils/unitConversion";

const Container = styled(Card, {
  shouldForwardProp: (prop) => prop !== "disableElevationAnimation",
})<{ disableElevationAnimation: boolean }>(
  ({ theme, disableElevationAnimation }) => ({
    transition: disableElevationAnimation ? "none" : "all 0.2s ease-out",
    "&:hover": disableElevationAnimation
      ? {}
      : {
          transform: "translateY(-4px)",
          boxShadow: theme.shadows[5],
        },
    gap: "15px",
    alignSelf: "stretch",
    borderRadius: "12px",
    border: disableElevationAnimation
      ? `1px solid ${theme.palette.primary.L70}`
      : `1px solid ${theme.palette.support[300]}`,
  }),
);

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { DataCardProps } from '@monthly/ui' */
export interface DataCardProps {
  potentialSavings?: PotentialSavingsData | null;
  /** Action handler for the potential savings card. The action button is omitted when not provided. */
  onPotentialSavingsClick?: () => void;
  label: string;
  value: number;
  period: string;
  currencySymbol: string;
  units: (typeof UNIDADES_OPTIONS)[number];
  lineColor?: string;
  frequency: string;
  currency: string;
  comparable: string;
  analysis: string;
  decimals?: number;
  /**
   * Indicates whether to disable the elevation effect on hover.
   * @default false
   */
  disableElevationAnimation?: boolean;
}

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { DataCard } from '@monthly/ui' */
export function DataCard({
  label,
  value,
  period,
  currencySymbol,
  units,
  lineColor,
  frequency,
  currency,
  comparable,
  analysis,
  disableElevationAnimation = false,
  decimals = 2,
  potentialSavings,
  onPotentialSavingsClick,
}: DataCardProps) {
  // Format the value based on decimals prop
  const formattedValue = formatNumberWithSeparators(value, decimals);

  return (
    <Container
      key={label}
      elevation={disableElevationAnimation ? 0 : 2}
      disableElevationAnimation={disableElevationAnimation}
    >
      <Stack p="20px" gap={2}>
        <Stack gap={1}>
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <TimelineRounded color="secondary" fontSize="small" />
            <Typography variant="h5">{label}</Typography>
          </Stack>
          <Typography variant="overline">
            {frequency} | {period} | {comparable} | {currency} |{" "}
            {analysis === "horizontal" ? "$" : "%"}
          </Typography>
        </Stack>
        <Divider
          sx={(theme) => ({
            borderColor: lineColor || theme.palette.primary.L70,
          })}
        />
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="body1"
            sx={(theme) => ({
              color: theme.palette.secondary.dark,
            })}
          >
            {period}
          </Typography>
          <Stack direction="row" alignItems="center">
            {analysis === "horizontal" && (
              <Typography variant="body1" component="span">
                {currencySymbol}
              </Typography>
            )}
            <Typography variant="h2">{formattedValue}</Typography>
            <Typography variant="body1" component="span">
              {analysis === "horizontal" ? units : "%"}
            </Typography>
          </Stack>
        </Stack>
        {potentialSavings && (
          <AskMaraCard
            title={`Ahorra hasta: `}
            action={
              onPotentialSavingsClick
                ? {
                    label: "Descubre como",
                    onClick: onPotentialSavingsClick,
                  }
                : undefined
            }
            value={potentialSavings?.amountFmt || 0}
            prefix={currencySymbol}
            unit={units}
            valueIcon={<SouthOutlined />} //TODO: ask for this static icon
            hasMagicStars={true}
          />
        )}
      </Stack>
    </Container>
  );
}
